/**
 * Media API, and the browser half of a direct-to-storage upload.
 *
 * THE FILE NEVER GOES TO THE ENGINE. The engine issues a capability; the browser PUTs the
 * bytes straight to object storage; the engine then verifies the object and marks the
 * asset ready. So a 400 MB video costs the engine two small JSON requests.
 */

import { api, ApiError } from './apiClient';

export type MediaVisibility = 'public' | 'private';
export type MediaKind = 'image' | 'video' | 'document' | 'other';
export type MediaStatus = 'pending' | 'ready' | 'failed' | 'deleted';

export interface MediaAsset {
  id: string;
  scope: 'organization' | 'platform';
  organizationId: string | null;
  organizationName?: string;
  uploaderEmail?: string;
  visibility: MediaVisibility;
  originalFilename: string | null;
  displayName: string;
  mimeType: string;
  kind: MediaKind;
  byteSize: number | null;
  sha256: string | null;
  sha256Verified: boolean;
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
  status: MediaStatus;
  createdAt: string;
  finalizedAt: string | null;
  deletedAt: string | null;
}

export interface MediaConfig {
  configured: boolean;
  allowedMimeTypes: string[];
  limits: Record<string, { kind: MediaKind; maxBytes: number }>;
}

export interface UploadAuthorization {
  asset: MediaAsset;
  upload: {
    url: string;
    method: string;
    headers: Record<string, string>;
    expiresAt: string;
  };
}

const q = (params: Record<string, string | null | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
  const out = s.toString();
  return out ? `?${out}` : '';
};

export const getMediaConfig = () => api.get<MediaConfig>('/admin/media/config');

export const listMedia = (params: {
  organizationId?: string | null; scope?: string; kind?: string;
  status?: string; search?: string;
} = {}) => api.get<MediaAsset[]>(`/admin/media${q(params)}`);

export const getMediaUrl = (id: string, download = false) =>
  api.get<{ url: string; expiresAt: string | null; visibility: MediaVisibility }>(
    `/admin/media/${id}/url${download ? '?download=true' : ''}`);

export const updateMedia = (id: string, body: { displayName?: string; visibility?: MediaVisibility }) =>
  api.patch<MediaAsset>(`/admin/media/${id}`, body);

/** Soft delete by default; `purge` also removes the bytes. History is kept either way. */
export const deleteMedia = (id: string, purge = false) =>
  api.delete<MediaAsset>(`/admin/media/${id}${purge ? '?purge=true' : ''}`);

const authorizeUpload = (body: {
  scope: string; organizationId: string | null; filename: string; mimeType: string;
  byteSize: number; sha256: string; visibility: MediaVisibility; displayName?: string;
}) => api.post<UploadAuthorization>('/admin/media/uploads', body);

const finalizeUpload = (id: string, body: {
  width?: number | null; height?: number | null; durationSeconds?: number | null;
}) => api.post<MediaAsset>(`/admin/media/${id}/finalize`, body);

/**
 * SHA-256 of the file, in the browser, before anything is uploaded.
 *
 * Sent with the authorization request so the engine can SIGN it into the upload. The
 * store then refuses bytes that do not hash to it, which is what turns the recorded hash
 * into a verified fact rather than a claim this code made about itself.
 *
 * crypto.subtle requires a secure context: https, or localhost. Absent one, the upload
 * still works and the hash is simply not declared — the asset is then recorded with
 * sha256Verified false, which is honest rather than broken.
 */
export async function hashFile(file: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/** Intrinsic dimensions, for layout reservation. Presentation metadata only. */
export async function measureImage(file: File): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith('image/')) return null;
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export class UploadError extends Error {
  readonly stage: 'authorize' | 'transfer' | 'finalize';
  constructor(stage: 'authorize' | 'transfer' | 'finalize', message: string) {
    super(message);
    this.stage = stage;
  }
}

/**
 * Authorize, transfer, finalize.
 *
 * THE UPLOAD IS NOT DONE WHEN THE PUT RETURNS 200. A store accepting bytes says nothing
 * about whether the asset is usable; only finalize, which asks the store what it actually
 * holds, can do that. So this resolves on the FINALIZED asset or throws, and the stage is
 * carried on the error because "storage rejected the transfer" and "the engine refused to
 * finalize" are different problems with different fixes.
 *
 * XMLHttpRequest rather than fetch, for one reason: upload progress. fetch still has no
 * way to report how far a request body has been sent.
 */
export async function uploadFile({
  file, organizationId, scope = 'organization', visibility = 'private',
  displayName, onProgress,
}: {
  file: File;
  organizationId: string | null;
  scope?: string;
  visibility?: MediaVisibility;
  displayName?: string;
  onProgress?: (fraction: number) => void;
}): Promise<MediaAsset> {
  const sha256 = await hashFile(file);
  const dimensions = await measureImage(file);

  let authorized: UploadAuthorization;
  try {
    authorized = await authorizeUpload({
      scope,
      organizationId,
      filename: file.name,
      mimeType: file.type,
      byteSize: file.size,
      sha256: sha256 ?? '',
      visibility,
      displayName,
    });
  } catch (err) {
    throw new UploadError('authorize',
      err instanceof ApiError ? err.message : 'Could not authorize the upload.');
  }

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(authorized.upload.method, authorized.upload.url, true);
    for (const [name, value] of Object.entries(authorized.upload.headers)) {
      // content-length is set by the browser from the body and cannot be assigned; the
      // engine signs it so that the store enforces it, not so that we send it.
      if (name.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new UploadError('transfer', `Storage rejected the upload (${xhr.status}).`));
    };
    xhr.onerror = () => reject(new UploadError('transfer', 'The transfer failed.'));
    xhr.onabort = () => reject(new UploadError('transfer', 'The transfer was cancelled.'));
    xhr.send(file);
  });

  try {
    return await finalizeUpload(authorized.asset.id, {
      width: dimensions?.width ?? null,
      height: dimensions?.height ?? null,
    });
  } catch (err) {
    throw new UploadError('finalize',
      err instanceof ApiError ? err.message : 'The upload could not be verified.');
  }
}

/** Human-readable size. */
export function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
